import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  BellRing,
  CheckCheck,
  Info,
  Lock,
  Megaphone,
  MoreHorizontal,
  Send,
  Waypoints,
} from "lucide-react";
import type {
  NotificationCategory,
  NotificationItem,
  NotificationSettings,
} from "@/api/notifications";
import {
  useMarkNotificationsRead,
  useNotificationSettings,
  useRemoveNotification,
  useUpdateNotificationSettings,
} from "@/hooks/useNotifications";

export const CATEGORY_META: Record<
  NotificationCategory,
  { label: string; icon: React.ReactNode; tile: string; dot: string }
> = {
  PRICE_ALERTS: { label: "Price Alerts", icon: <Activity />, tile: "bg-[#053321] text-[#2dcc89]", dot: "bg-[#2dcc89]" },
  TRADES: { label: "Trades", icon: <Waypoints />, tile: "bg-[#2c1c5f] text-[#b692f6]", dot: "bg-[#a855f7]" },
  LISTINGS: { label: "Listings", icon: <Megaphone />, tile: "bg-[#4e1d09] text-[#fdb022]", dot: "bg-[#f79009]" },
  DELISTINGS: { label: "Delistings", icon: <Lock />, tile: "bg-white/[0.06] text-[#a3a3a3]", dot: "bg-[#a3a3a3]" },
  SYSTEM: { label: "System", icon: <Info />, tile: "bg-[#102a56] text-[#53b1fd]", dot: "bg-[#3b82f6]" },
};

export const FILTERS: Array<{ value: NotificationCategory | null; label: string }> = [
  { value: null, label: "All" },
  { value: "PRICE_ALERTS", label: "Price Alerts" },
  { value: "TRADES", label: "Trades" },
  { value: "LISTINGS", label: "Listings" },
  { value: "DELISTINGS", label: "Delistings" },
  { value: "SYSTEM", label: "System" },
];

export function timeAgo(iso: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

/** Category pills; they scroll sideways on phones. */
export const NotificationFilters: React.FC<{
  value: NotificationCategory | null;
  onChange: (value: NotificationCategory | null) => void;
  mobile?: boolean;
}> = ({ value, onChange, mobile }) => (
  <div className={`flex gap-2 overflow-x-auto [scrollbar-width:none] ${mobile ? "-mx-4 px-4 pb-1" : ""}`}>
    {FILTERS.map((filter) => {
      const active = filter.value === value;
      return (
        <button
          key={filter.label}
          type="button"
          onClick={() => onChange(filter.value)}
          aria-pressed={active}
          className={`shrink-0 whitespace-nowrap rounded-xl border font-semibold transition-colors ${
            mobile ? "h-10 px-3 text-sm" : "h-11 flex-1 px-4 text-base"
          } ${
            active
              ? "border-[#2dcc89] bg-[#0a0a0a] text-white shadow-[0_0_12px_rgba(45,204,137,0.25)]"
              : "border-white/10 bg-[#0d121a]/70 text-[#a3a3a3] hover:text-white"
          }`}
        >
          {filter.label}
        </button>
      );
    })}
  </div>
);

export const NotificationRow: React.FC<{ item: NotificationItem; userId: string; mobile?: boolean }> = ({
  item,
  userId,
  mobile,
}) => {
  const navigate = useNavigate();
  const markRead = useMarkNotificationsRead(userId);
  const remove = useRemoveNotification(userId);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const meta = CATEGORY_META[item.category];
  const isListing = item.id.startsWith("alert:");

  useEffect(() => {
    if (!menu) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const open = () => {
    if (!item.read && !isListing) markRead.mutate(item.id);
    if (item.link) navigate(item.link);
  };

  return (
    <div
      className={`relative flex items-center gap-3 rounded-xl border border-white/10 bg-[#0d121a]/80 ${mobile ? "p-2.5" : "px-3 py-3"} ${
        item.read ? "" : "border-white/15"
      }`}
    >
      <span className={`relative flex shrink-0 items-center justify-center rounded-lg ${mobile ? "size-10 [&>svg]:size-5" : "size-12 [&>svg]:size-6"} ${meta.tile}`}>
        {meta.icon}
        {!item.read && <span className={`absolute -right-0.5 -top-0.5 size-2 rounded-full ${meta.dot}`} aria-label="Unread" />}
      </span>
      <button type="button" onClick={open} className="flex min-w-0 flex-1 flex-col text-left">
        <span className={`font-semibold text-white ${mobile ? "text-sm leading-5" : "text-base leading-6"}`}>{item.title}</span>
        <span className={`text-[#a3a3a3] ${mobile ? "line-clamp-2 text-[11px] leading-4" : "text-sm leading-5"}`}>{item.body}</span>
      </button>
      <span className="shrink-0 whitespace-nowrap text-xs text-[#a3a3a3] sm:text-sm">{timeAgo(item.createdAt)}</span>
      {!mobile && item.link && (
        <button
          type="button"
          onClick={open}
          className="shrink-0 rounded-lg border border-white/10 px-3 py-1.5 text-sm font-semibold text-[#d4d4d4] transition-colors hover:border-white/25"
        >
          View
        </button>
      )}
      <div ref={menuRef} className="relative shrink-0">
        <button
          type="button"
          aria-label="More actions"
          onClick={() => setMenu((v) => !v)}
          className="flex size-8 items-center justify-center rounded-lg text-[#a3a3a3] hover:bg-white/5 hover:text-white"
        >
          <MoreHorizontal className="size-5" />
        </button>
        {menu && (
          <div className="absolute right-0 top-9 z-20 w-40 overflow-hidden rounded-lg border border-white/10 bg-[#111827] py-1 shadow-xl">
            {mobile && item.link && (
              <button type="button" onClick={open} className="block w-full px-3 py-2 text-left text-sm text-white hover:bg-white/5">
                View
              </button>
            )}
            {!item.read && !isListing && (
              <button
                type="button"
                onClick={() => {
                  markRead.mutate(item.id);
                  setMenu(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm text-white hover:bg-white/5"
              >
                Mark as read
              </button>
            )}
            {!isListing && (
              <button
                type="button"
                onClick={() => {
                  remove.mutate(item.id);
                  setMenu(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm text-[#f97066] hover:bg-white/5"
              >
                Remove
              </button>
            )}
            {isListing && (
              <p className="px-3 py-2 text-xs text-[#a3a3a3]">Listings clear with "Mark all as read".</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const MarkAllReadButton: React.FC<{ userId: string; disabled?: boolean }> = ({ userId, disabled }) => {
  const markRead = useMarkNotificationsRead(userId);
  return (
    <button
      type="button"
      disabled={disabled || markRead.isPending}
      onClick={() => markRead.mutate(undefined)}
      className="flex items-center gap-1.5 text-sm font-semibold text-[#d4d4d4] transition-colors hover:text-white disabled:opacity-40 sm:text-base"
    >
      <CheckCheck className="size-5" />
      Mark all as read
    </button>
  );
};

/** On/off switch in the redesign green. */
export const Toggle: React.FC<{ checked: boolean; onChange: (value: boolean) => void; label: string }> = ({
  checked,
  onChange,
  label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-[#07a66c]" : "bg-white/15"}`}
  >
    <span
      className={`absolute left-0 top-0.5 size-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`}
    />
  </button>
);

const TYPE_ROWS: Array<{ key: keyof NotificationSettings; category: NotificationCategory; title: string; text: string }> = [
  { key: "priceAlerts", category: "PRICE_ALERTS", title: "Price Alerts", text: "Get notified when a price alert you set fires." },
  { key: "trades", category: "TRADES", title: "Trade Notifications", text: "Opened and closed positions, autotrade results." },
  { key: "listings", category: "LISTINGS", title: "Listings", text: "Get notified about new listings." },
  { key: "delistings", category: "DELISTINGS", title: "Delistings", text: "Get notified about upcoming delistings." },
  { key: "system", category: "SYSTEM", title: "System Notifications", text: "Exchange connection, balance and API key warnings." },
];

const CHANNEL_ROWS: Array<{ key: keyof NotificationSettings; icon: React.ReactNode; title: string; text: string }> = [
  { key: "inApp", icon: <BellRing />, title: "In-App Notifications", text: "Show them here and on the bell." },
  { key: "telegram", icon: <Send />, title: "Telegram Notifications", text: "Receive them as messages from the Trigger bot." },
];

const SettingRow: React.FC<{
  icon: React.ReactNode;
  tile: string;
  title: string;
  text: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ icon, tile, title, text, checked, onChange }) => (
  <div className="flex items-center gap-3">
    <span className={`flex size-10 shrink-0 items-center justify-center rounded-lg [&>svg]:size-5 ${tile}`}>{icon}</span>
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="text-sm font-semibold leading-5 text-white">{title}</span>
      <span className="text-xs leading-[18px] text-[#a3a3a3]">{text}</span>
    </div>
    <Toggle checked={checked} onChange={onChange} label={title} />
  </div>
);

/** "Notification Settings" (types) or "Notification Channels" list of switches. */
export const NotificationSettingsList: React.FC<{ userId: string; kind: "types" | "channels" }> = ({ userId, kind }) => {
  const { data } = useNotificationSettings(userId);
  const update = useUpdateNotificationSettings(userId);
  if (!data) return <div className="h-40 animate-pulse rounded-xl bg-white/[0.03]" />;
  return (
    <div className="flex flex-col gap-5">
      {kind === "types"
        ? TYPE_ROWS.map((row) => (
            <SettingRow
              key={row.key}
              icon={CATEGORY_META[row.category].icon}
              tile={CATEGORY_META[row.category].tile}
              title={row.title}
              text={row.text}
              checked={data[row.key]}
              onChange={(value) => update.mutate({ [row.key]: value })}
            />
          ))
        : CHANNEL_ROWS.map((row) => (
            <SettingRow
              key={row.key}
              icon={row.icon}
              tile="bg-[#102a56] text-[#53b1fd]"
              title={row.title}
              text={row.text}
              checked={data[row.key]}
              onChange={(value) => update.mutate({ [row.key]: value })}
            />
          ))}
    </div>
  );
};
